import { useState, useMemo } from 'react'
import { useFinance } from '../../context/FinanceContext'
import { getThemeColors } from '../../lib/constants'
import { formatCurrency, formatPercent } from '../../lib/formatters'
import { calculateHomeAffordability } from '../../lib/calculations/homeAffordability'
import GlassCard from '../ui/GlassCard'
import Button from '../ui/Button'
import { useSubscription } from '../../context/SubscriptionContext'
import { trackExportReport } from '../../lib/analytics'

function InputField({ label, value, onChange, prefix, suffix, type = 'number', hint, step }) {
  return (
    <div>
      <label className="block text-xs font-medium text-muted mb-1.5">{label}</label>
      <div className="relative">
        {prefix && <span className="absolute left-3 top-1/2 -translate-y-1/2 text-faint text-sm">{prefix}</span>}
        <input
          type={type}
          value={value}
          onChange={e => onChange(parseFloat(e.target.value) || 0)}
          step={step}
          className={`w-full bg-input border border-input rounded-xl py-2.5 text-sm text-heading focus:outline-none focus:border-primary transition ${prefix ? 'pl-7' : 'pl-3'} ${suffix ? 'pr-12' : 'pr-3'}`}
        />
        {suffix && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-faint text-sm">{suffix}</span>}
      </div>
      {hint && <p className="text-xs text-faint mt-1">{hint}</p>}
    </div>
  )
}

function ResultCard({ label, value, color, highlight }) {
  return (
    <GlassCard className={`!p-4 ${highlight ? 'ring-1' : ''}`} style={highlight ? { borderColor: color + '44' } : {}}>
      <p className="text-xs text-muted mb-1">{label}</p>
      <p className={`font-bold ${highlight ? 'text-xl' : 'text-lg'}`} style={{ color }}>{value}</p>
    </GlassCard>
  )
}

export default function HomeAffordability() {
  const { state } = useFinance()
  const { isPro } = useSubscription()
  const colors = getThemeColors(state.theme)

  const [grossMonthlyIncome, setGrossMonthlyIncome] = useState(8000)
  const [monthlyDebts, setMonthlyDebts] = useState(500)
  const [downPayment, setDownPayment] = useState(60000)
  const [interestRate, setInterestRate] = useState(6.5)
  const [loanTerm, setLoanTerm] = useState(360)
  const [propertyTaxRate, setPropertyTaxRate] = useState(1.2)
  const [annualInsurance, setAnnualInsurance] = useState(1800)
  const [monthlyHOA, setMonthlyHOA] = useState(0)
  const [pmiRate, setPmiRate] = useState(0.5)

  const result = useMemo(() => calculateHomeAffordability({
    grossMonthlyIncome, monthlyDebts, downPayment, interestRate, loanTerm,
    propertyTaxRate, annualInsurance, monthlyHOA, pmiRate,
  }), [grossMonthlyIncome, monthlyDebts, downPayment, interestRate, loanTerm, propertyTaxRate, annualInsurance, monthlyHOA, pmiRate])

  const handleExportPDF = async () => {
    trackExportReport()
    const { default: jsPDF } = await import('jspdf')
    const { default: autoTable } = await import('jspdf-autotable')

    const doc = new jsPDF()
    const margin = 14

    const tableStyles = {
      theme: 'grid',
      styles: { fontSize: 9, cellPadding: 3 },
      headStyles: { fillColor: [41, 65, 122], textColor: [255, 255, 255], fontStyle: 'bold' },
      alternateRowStyles: { fillColor: [245, 247, 250] },
    }

    // --- Page 1: Title, Inputs, Key Results ---

    // Title
    doc.setFontSize(22)
    doc.setTextColor(30, 30, 30)
    doc.text('Home Affordability Report', margin, 22)

    doc.setFontSize(9)
    doc.setTextColor(120, 120, 120)
    doc.text(`Generated by Liquid Finance  |  ${new Date().toLocaleDateString()}`, margin, 30)

    // Income & Debts
    doc.setFontSize(13)
    doc.setTextColor(30, 30, 30)
    doc.text('Income & Debts', margin, 42)

    const incomeRows = [
      ['Gross Monthly Income', formatCurrency(grossMonthlyIncome)],
      ['Annual Income', formatCurrency(grossMonthlyIncome * 12)],
      ['Monthly Debt Payments', formatCurrency(monthlyDebts)],
      ['Down Payment', formatCurrency(downPayment)],
    ]

    autoTable(doc, {
      body: incomeRows,
      startY: 46,
      ...tableStyles,
      columnStyles: { 0: { fontStyle: 'bold', cellWidth: 80 } },
    })

    // Loan & Home Costs
    let y = doc.lastAutoTable.finalY + 10
    doc.setFontSize(13)
    doc.setTextColor(30, 30, 30)
    doc.text('Loan & Home Costs', margin, y)

    const loanRows = [
      ['Mortgage Rate', `${interestRate}%`],
      ['Loan Term', `${loanTerm / 12} years (${loanTerm} months)`],
      ['Property Tax Rate', `${propertyTaxRate}%`],
      ['Annual Insurance', formatCurrency(annualInsurance)],
      ['Monthly HOA', formatCurrency(monthlyHOA)],
      ['PMI Rate', `${pmiRate}% (waived with 20%+ down)`],
    ]

    autoTable(doc, {
      body: loanRows,
      startY: y + 4,
      ...tableStyles,
      columnStyles: { 0: { fontStyle: 'bold', cellWidth: 80 } },
    })

    // Key Results
    y = doc.lastAutoTable.finalY + 10
    doc.setFontSize(13)
    doc.setTextColor(30, 30, 30)
    doc.text('Key Results', margin, y)

    const resultRows = [
      ['Max Home Price', formatCurrency(result.maxHomePrice)],
      ['Total Monthly Payment', formatCurrency(result.totalMonthlyPayment)],
      ['Front-End DTI', `${result.frontEndDTI}%`],
      ['Back-End DTI', `${result.backEndDTI}%`],
      ['Loan Amount', formatCurrency(result.maxLoanAmount)],
      ['Down Payment %', `${result.downPaymentPercent}%`],
      ['20% Down Amount', formatCurrency(Math.round(result.maxHomePrice * 0.20))],
      ['PMI Required', result.needsPMI ? 'Yes' : 'No'],
      ['Total Interest (over loan life)', formatCurrency(result.totalInterest)],
    ]

    autoTable(doc, {
      body: resultRows,
      startY: y + 4,
      ...tableStyles,
      columnStyles: { 0: { fontStyle: 'bold', cellWidth: 80 } },
    })

    // --- Page 2: Payment Breakdown, DTI Guidelines ---
    doc.addPage()

    // Monthly Payment Breakdown
    doc.setFontSize(16)
    doc.setTextColor(30, 30, 30)
    doc.text('Monthly Payment Breakdown', margin, 22)

    const breakdownRows = [
      ['Principal & Interest', formatCurrency(result.monthlyPI), result.totalMonthlyPayment > 0 ? `${((result.monthlyPI / result.totalMonthlyPayment) * 100).toFixed(1)}%` : '0%'],
      ['Property Tax', formatCurrency(result.monthlyPropertyTax), result.totalMonthlyPayment > 0 ? `${((result.monthlyPropertyTax / result.totalMonthlyPayment) * 100).toFixed(1)}%` : '0%'],
      ["Homeowner's Insurance", formatCurrency(result.monthlyInsurance), result.totalMonthlyPayment > 0 ? `${((result.monthlyInsurance / result.totalMonthlyPayment) * 100).toFixed(1)}%` : '0%'],
    ]
    if (result.needsPMI) {
      breakdownRows.push(['PMI', formatCurrency(result.monthlyPMI), result.totalMonthlyPayment > 0 ? `${((result.monthlyPMI / result.totalMonthlyPayment) * 100).toFixed(1)}%` : '0%'])
    }
    if (result.monthlyHOA > 0) {
      breakdownRows.push(['HOA', formatCurrency(result.monthlyHOA), result.totalMonthlyPayment > 0 ? `${((result.monthlyHOA / result.totalMonthlyPayment) * 100).toFixed(1)}%` : '0%'])
    }
    breakdownRows.push(['Total Monthly Payment', formatCurrency(result.totalMonthlyPayment), '100%'])

    autoTable(doc, {
      head: [['Component', 'Monthly Amount', '% of Total']],
      body: breakdownRows,
      startY: 28,
      ...tableStyles,
      columnStyles: { 0: { fontStyle: 'bold', cellWidth: 65 }, 2: { halign: 'center' } },
      didParseCell: (data) => {
        if (data.section === 'body' && data.row.index === breakdownRows.length - 1) {
          data.cell.styles.fontStyle = 'bold'
          data.cell.styles.fillColor = [230, 240, 255]
        }
      },
    })

    // DTI Guidelines
    y = doc.lastAutoTable.finalY + 14
    doc.setFontSize(16)
    doc.setTextColor(30, 30, 30)
    doc.text('DTI Guidelines', margin, y)

    const dtiRows = [
      [
        'Front-End DTI (28% Rule)',
        `${result.frontEndDTI}%`,
        '28%',
        result.frontEndDTI <= 28 ? 'PASS' : 'OVER LIMIT',
        `Housing costs only. Max: ${formatCurrency(result.maxFrontEnd)}/mo`,
      ],
      [
        'Back-End DTI (36% Rule)',
        `${result.backEndDTI}%`,
        '36%',
        result.backEndDTI <= 36 ? 'PASS' : 'OVER LIMIT',
        `Housing + all debts. Max: ${formatCurrency(result.maxBackEnd)}/mo`,
      ],
    ]

    autoTable(doc, {
      head: [['DTI Metric', 'Your DTI', 'Limit', 'Status', 'Description']],
      body: dtiRows,
      startY: y + 4,
      ...tableStyles,
      columnStyles: { 0: { fontStyle: 'bold', cellWidth: 42 }, 1: { halign: 'center' }, 2: { halign: 'center' }, 3: { halign: 'center', cellWidth: 24 } },
      didParseCell: (data) => {
        if (data.section === 'body' && data.column.index === 3) {
          data.cell.styles.textColor = data.cell.raw === 'PASS' ? [22, 163, 74] : [220, 38, 38]
          data.cell.styles.fontStyle = 'bold'
        }
      },
    })

    // DTI Budget Summary
    y = doc.lastAutoTable.finalY + 14
    doc.setFontSize(13)
    doc.setTextColor(30, 30, 30)
    doc.text('DTI Budget Summary', margin, y)

    const budgetRows = [
      ['Gross Monthly Income', formatCurrency(grossMonthlyIncome)],
      ['Max Front-End Housing Payment (28%)', formatCurrency(result.maxFrontEnd)],
      ['Max Back-End Total Payments (36%)', formatCurrency(result.maxBackEnd)],
      ['Max Housing from Back-End (after debts)', formatCurrency(result.maxHousingFromBackEnd)],
      ['Binding Constraint', result.maxFrontEnd <= result.maxHousingFromBackEnd ? 'Front-End (28% Rule)' : 'Back-End (36% Rule minus debts)'],
      ['Your Monthly Debts', formatCurrency(monthlyDebts)],
    ]

    autoTable(doc, {
      body: budgetRows,
      startY: y + 4,
      ...tableStyles,
      columnStyles: { 0: { fontStyle: 'bold', cellWidth: 90 } },
    })

    // PMI Note
    if (result.needsPMI) {
      y = doc.lastAutoTable.finalY + 10
      doc.setFontSize(10)
      doc.setTextColor(80, 80, 80)
      doc.text(`Note: PMI of ${formatCurrency(result.monthlyPMI)}/mo applies until you reach 20% equity.`, margin, y)
      doc.text(`Put ${formatCurrency(Math.round(result.maxHomePrice * 0.20))} down to avoid PMI entirely.`, margin, y + 7)
    }

    doc.save(`home-affordability-${Date.now()}.pdf`)
  }

  return (
    <div className="space-y-6">
      {/* Inputs */}
      <GlassCard>
        <div className="flex items-start justify-between gap-4 mb-4">
          <h3 className="text-lg font-semibold text-heading">Income & Debts</h3>
          <Button variant="secondary" size="sm" onClick={handleExportPDF} disabled={!isPro} title={!isPro ? 'Pro feature' : ''}>
            Export PDF{!isPro ? ' (Pro)' : ''}
          </Button>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <InputField label="Gross Monthly Income" value={grossMonthlyIncome} onChange={setGrossMonthlyIncome} prefix="$" />
          <InputField label="Monthly Debt Payments" value={monthlyDebts} onChange={setMonthlyDebts} prefix="$" hint="Car, student loans, credit cards, etc" />
          <InputField label="Down Payment" value={downPayment} onChange={setDownPayment} prefix="$" />
        </div>
      </GlassCard>

      <GlassCard>
        <h3 className="text-lg font-semibold text-heading mb-4">Loan & Home Costs</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <InputField label="Mortgage Rate" value={interestRate} onChange={setInterestRate} suffix="%" step={0.125} />
          <div>
            <label className="block text-xs font-medium text-muted mb-1.5">Loan Term</label>
            <div className="flex gap-1">
              {[180, 240, 360].map(t => (
                <button
                  key={t}
                  onClick={() => setLoanTerm(t)}
                  className={`flex-1 px-2 py-2 rounded-xl text-xs font-medium transition-all ${
                    loanTerm === t ? 'text-heading shadow-sm' : 'text-muted hover:text-heading'
                  }`}
                  style={loanTerm === t ? {
                    background: `linear-gradient(135deg, ${colors.primary}22, ${colors.secondary}22)`,
                    border: `1px solid ${colors.primary}44`,
                  } : { border: '1px solid transparent' }}
                >
                  {t / 12}yr
                </button>
              ))}
            </div>
          </div>
          <InputField label="Property Tax Rate" value={propertyTaxRate} onChange={setPropertyTaxRate} suffix="%" step={0.1} />
          <InputField label="Annual Insurance" value={annualInsurance} onChange={setAnnualInsurance} prefix="$" />
          <InputField label="Monthly HOA" value={monthlyHOA} onChange={setMonthlyHOA} prefix="$" />
          <InputField label="PMI Rate" value={pmiRate} onChange={setPmiRate} suffix="%" step={0.1} hint="Waived with 20%+ down" />
        </div>
      </GlassCard>

      {/* Key Results */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <ResultCard label="Max Home Price" value={formatCurrency(result.maxHomePrice)} color={colors.positive} highlight />
        <ResultCard label="Monthly Payment" value={formatCurrency(result.totalMonthlyPayment)} color={colors.primary} highlight />
        <ResultCard label="Front-End DTI" value={`${result.frontEndDTI}%`} color={result.frontEndDTI <= 28 ? colors.positive : colors.warning} />
        <ResultCard label="Back-End DTI" value={`${result.backEndDTI}%`} color={result.backEndDTI <= 36 ? colors.positive : colors.warning} />
      </div>

      {/* Payment Breakdown */}
      <GlassCard>
        <h3 className="text-lg font-semibold text-heading mb-4">Monthly Payment Breakdown</h3>
        <div className="space-y-3">
          <BreakdownRow label="Principal & Interest" amount={result.monthlyPI} color={colors.primary} total={result.totalMonthlyPayment} />
          <BreakdownRow label="Property Tax" amount={result.monthlyPropertyTax} color={colors.warning} total={result.totalMonthlyPayment} />
          <BreakdownRow label="Homeowner's Insurance" amount={result.monthlyInsurance} color={colors.secondary} total={result.totalMonthlyPayment} />
          {result.needsPMI && (
            <BreakdownRow label="PMI" amount={result.monthlyPMI} color={colors.negative} total={result.totalMonthlyPayment} />
          )}
          {result.monthlyHOA > 0 && (
            <BreakdownRow label="HOA" amount={result.monthlyHOA} color={colors.warning} total={result.totalMonthlyPayment} />
          )}
          <div className="border-t border-card pt-2 flex justify-between text-sm font-semibold">
            <span className="text-heading">Total Monthly</span>
            <span style={{ color: colors.primary }}>{formatCurrency(result.totalMonthlyPayment)}</span>
          </div>
        </div>
      </GlassCard>

      {/* DTI Guidelines */}
      <GlassCard>
        <h3 className="text-lg font-semibold text-heading mb-4">DTI Guidelines</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="p-4 rounded-xl border border-card">
            <h4 className="text-sm font-semibold text-heading mb-2">Front-End DTI (28% Rule)</h4>
            <p className="text-xs text-muted mb-2">Housing costs only. Max: {formatCurrency(result.maxFrontEnd)}/mo</p>
            <div className="w-full h-3 rounded-full bg-subtle overflow-hidden">
              <div className="h-full rounded-full transition-all" style={{
                width: `${Math.min(100, result.frontEndDTI / 28 * 100)}%`,
                backgroundColor: result.frontEndDTI <= 28 ? colors.positive : colors.negative,
              }} />
            </div>
          </div>
          <div className="p-4 rounded-xl border border-card">
            <h4 className="text-sm font-semibold text-heading mb-2">Back-End DTI (36% Rule)</h4>
            <p className="text-xs text-muted mb-2">Housing + all debts. Max: {formatCurrency(result.maxBackEnd)}/mo</p>
            <div className="w-full h-3 rounded-full bg-subtle overflow-hidden">
              <div className="h-full rounded-full transition-all" style={{
                width: `${Math.min(100, result.backEndDTI / 36 * 100)}%`,
                backgroundColor: result.backEndDTI <= 36 ? colors.positive : colors.negative,
              }} />
            </div>
          </div>
        </div>
        {result.needsPMI && (
          <p className="text-xs text-muted mt-3">
            PMI of {formatCurrency(result.monthlyPMI)}/mo applies until you reach 20% equity. Put {formatCurrency(Math.round(result.maxHomePrice * 0.20))} down to avoid PMI.
          </p>
        )}
      </GlassCard>
    </div>
  )
}

function BreakdownRow({ label, amount, color, total }) {
  const pct = total > 0 ? (amount / total) * 100 : 0
  return (
    <div className="flex items-center gap-3">
      <span className="text-sm text-muted flex-1">{label}</span>
      <div className="w-24 h-2 rounded-full bg-subtle overflow-hidden">
        <div className="h-full rounded-full transition-all duration-500" style={{ width: `${Math.min(100, pct)}%`, backgroundColor: color }} />
      </div>
      <span className="text-sm font-medium text-heading w-20 text-right">{formatCurrency(amount)}</span>
    </div>
  )
}
